import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export function useAppLogic() {
  // ============================================================================
  // 1. STATE MANAGEMENT
  // ============================================================================

  // --- Session & Core Data ---
  const [sessionId, setSessionId] = useState(null);
  const [projectName, setProjectName] = useState("Writ Petition 2025");
  const [currentView, setCurrentView] = useState('editor');

  // --- File & Section Data ---
  const [indexFile, setIndexFile] = useState(null);
  const [indexFileName, setIndexFileName] = useState(null);
  const [indexPageCount, setIndexPageCount] = useState(0);
  const [sections, setSections] = useState([]);
  const [uploadedSections, setUploadedSections] = useState({});
  const [sectionPageCounts, setSectionPageCounts] = useState({});
  const [skippedSections, setSkippedSections] = useState([]);
  const [activeItem, setActiveItem] = useState({ type: 'index', index: null });

  // --- Sidebar File Manager State ---
  const [sectionFiles, setSectionFiles] = useState([]);
  const [viewingFileUrl, setViewingFileUrl] = useState(null);

  // --- Status & Progress Flags ---
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState('');
  const [isModifying, setIsModifying] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergedDocument, setMergedDocument] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // --- UI Flags ---
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isThumbnailsOpen, setIsThumbnailsOpen] = useState(true);

  // --- PDF Viewer State ---
  const [scale, setScale] = useState(1.0);
  const [pageRotations, setPageRotations] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [overlays, setOverlays] = useState([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [showPageNumbers, setShowPageNumbers] = useState(true);

  // Per-page positions: { [pageNum]: { x, y } }
  // Each page tracks its own position independently.
  // Pages not yet dragged fall back to { x: 95, y: 4 } via getPageNumberPosition.
  const [pageNumberPositions, setPageNumberPositions] = useState({});
  const [pageNumberFontSize, setPageNumberFontSize] = useState(26);

  const imageInputRef = useRef(null);

  // ============================================================================
  // 2. COMPUTED VALUES & HELPERS
  // ============================================================================

  const getPreviewUrl = () => {
    let url = null;
    if (activeItem.type === 'index' && indexFileName)
      url = `${API_BASE_URL}/api/view/${sessionId}/${indexFileName}`;
    else if (activeItem.type === 'section' && activeItem.index !== null) {
      const sectionFile = uploadedSections[activeItem.index];
      if (sectionFile?.name) url = `${API_BASE_URL}/api/view/${sessionId}/${sectionFile.name}`;
    }
    else if (activeItem.type === 'merged' && mergedDocument)
      url = `${API_BASE_URL}/api/view/${sessionId}/${mergedDocument.merged_file}`;

    return url ? `${url}?t=${refreshKey}` : null;
  };

  const currentPreviewUrl = getPreviewUrl();

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const toggleThumbnails = () => setIsThumbnailsOpen(prev => !prev);
  const togglePageNumbers = () => setShowPageNumbers(prev => !prev);

  const calculateOverallProgress = () => {
    if (!sections|| sections.length === 0) {
      if (isUploading && !indexFile) return Math.min(extractionProgress * 0.1, 10);
      return indexFile ? 10 : 0;
    }
    const uploadedCount = Object.keys(uploadedSections).length;
    return Math.round(10 + (uploadedCount * (90 / sections.length)));
  };

  const refreshSectionPageCounts = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/section-page-counts/${sessionId}`);
      if (res.data.success) {
        setSectionPageCounts(res.data.page_counts);
      }
    } catch (err) {
      console.error("Failed to refresh section page counts:", err);
    }
  }, [sessionId]);

  // Add these refs right after your state declarations in Section 1:
const sectionsRef = useRef(sections);
const uploadedSectionsRef = useRef(uploadedSections);
const projectNameRef = useRef(projectName);

// Add these effects to keep refs in sync (add them in Section 5):
useEffect(() => { sectionsRef.current = sections; }, [sections]);
useEffect(() => { uploadedSectionsRef.current = uploadedSections; }, [uploadedSections]);
useEffect(() => { projectNameRef.current = projectName; }, [projectName]);

// Replace your syncManifest useCallback entirely with:
const syncManifest = useCallback(async (overrides = {}) => {
  if (!sessionId) return;
  try {
    await axios.post(`${API_BASE_URL}/api/update-manifest`, {
      session_id:        sessionId,
      project_name:      overrides.project_name      ?? projectNameRef.current,
      sections_total:    overrides.sections_total    ?? sectionsRef.current.length,
      sections_uploaded: overrides.sections_uploaded ?? Object.keys(uploadedSectionsRef.current).length,
      jurisdiction:      overrides.jurisdiction      ?? "",
      court_type:        overrides.court_type        ?? "Writ Petition",
      sections:          overrides.sections          ?? sectionsRef.current.map(s => ({
        name: s.name,
        page: s.page,
        found: s.found
      })),
    });
  } catch (err) {
    console.error("Manifest sync failed:", err);
  }
}, [sessionId]); // Only depends on sessionId now, refs handle the rest


  const pageOffset = useMemo(() => {
    if (activeItem.type !== 'section') return 0;
    let offset = 0;
    for (let i = 0; i < activeItem.index; i++) {
      const count = sectionPageCounts[String(i)];
      if (count) offset += count;
    }
    return offset;
  }, [activeItem, sectionPageCounts]);

  const showPageNumbersForActiveFile = useMemo(() => {
    if (activeItem.type === 'index') return false;
    if (activeItem.type === 'merged') return false;
    return showPageNumbers;
  }, [activeItem, showPageNumbers]);

  const canMerge = sections && sections.length > 0 && Object.keys(uploadedSections || {}).length === sections.length;
  const activeName = activeItem.type === 'section' ? sections?.[activeItem.index]?.name : 'Master Index';
  const isExtracting = isUploading && !indexFile;

  const currentFilename = useMemo(() => {
    if (activeItem.type === 'section') {
      if (!viewingFileUrl) return null;
      return viewingFileUrl.split('/').pop().split('?')[0];
    }
    if (activeItem.type === 'index') return indexFileName;
    return null;
  }, [activeItem, viewingFileUrl, indexFileName]);
  
  const setPageNumberPosition = useCallback((pageNum, pos) => {
    console.log("setPageNumberPosition called", { pageNum, pos, currentFilename });
    if (!currentFilename) return;
    setPageNumberPositions(prev => ({
      ...prev,
      [currentFilename]: {
        ...prev[currentFilename],
        [pageNum]: pos,
      }
    }));
  }, [currentFilename]);

  const getPageNumberPosition = useCallback((pageNum) => {
    if (!currentFilename) return { x: 95, y: 4 };
    return pageNumberPositions[currentFilename]?.[pageNum] || { x: 95, y: 4 };
  }, [pageNumberPositions, currentFilename]);

  const [jurisdiction, setJurisdiction] = useState("Delhi HC");
  // ============================================================================
  // 3. CORE LOGIC FUNCTIONS
  // ============================================================================

  const fetchSectionFiles = useCallback(async () => {
    if (activeItem.type !== 'section') return;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/section-files/${sessionId}/${activeItem.index}`);

      if (response.data.success) {
        const files = response.data.files;
        setSectionFiles(files);

        if (files.length > 0) {
          const newestFile = files[files.length - 1];
          const newUrl = `${API_BASE_URL}/api/view/${sessionId}/${newestFile}`;

          setViewingFileUrl(prev => {
            const currentName = prev ? prev.split('/').pop().split('?')[0] : "";
            return currentName !== newestFile ? newUrl : prev;
          });

          const ovRes = await axios.get(`${API_BASE_URL}/api/overlays?session_id=${sessionId}`);
          if (ovRes.data) {
            const activeOverlays = ovRes.data.filter(o => o.pdf_filename === newestFile);
            setOverlays(activeOverlays);
          }

          setSelectedPage(1);
        } else {
          setViewingFileUrl(null);
          setOverlays([]);
        }
      }
    } catch (error) {
      console.error("Error fetching section files:", error);
    }
  }, [sessionId, activeItem]);

  const handleInsertPageLocally = async () => {
    const activeUrl = activeItem.type === 'section' ? viewingFileUrl : currentPreviewUrl;

    if (!activeUrl || !selectedPage) {
      alert("No document selected or page not found.");
      return;
    }

    if (isModifying) return;
    setIsModifying(true);

    const activeFilename = activeUrl.split('/').pop().split('?')[0];

    try {
      const response = await axios.post(`${API_BASE_URL}/api/insert-blank-page`, {
        session_id: sessionId,
        filename: activeFilename,
        page_number: selectedPage
      });

      if (response.data.success) {
        const timestamp = new Date().getTime();

        if (activeItem.type === 'section') {
          setViewingFileUrl(prev => {
            const base = prev.split('?')[0];
            return `${base}?t=${timestamp}`;
          });
        }
        setSelectedPage(selectedPage + 1);

        if (activeItem.type === 'section') {
          fetchSectionFiles();
          refreshSectionPageCounts();
        }
      } else {
        alert("Failed to insert page: " + response.data.error);
      }
    } catch (error) {
      console.error("Error inserting page:", error);
      alert("Error contacting server.");
    } finally {
      setIsModifying(false);
    }
  };

  const handleDeleteCurrentPage = async () => {
    const activeUrl = activeItem.type === 'section' ? viewingFileUrl : currentPreviewUrl;

    if (!activeUrl || !selectedPage) {
      alert("No document selected or page not found.");
      return;
    }

    const activeFilename = activeUrl.split('/').pop().split('?')[0];

    try {
      const response = await axios.post(`${API_BASE_URL}/api/delete-page`, {
        session_id: sessionId,
        filename: activeFilename,
        page_number: selectedPage
      });

      if (response.data.success) {
        if (response.data.file_deleted) {
          if (activeItem.type === 'section') {
            setSections(prev => {
              const newSections = [...prev];
              if (newSections[activeItem.index]) {
                newSections[activeItem.index] = { ...newSections[activeItem.index], uploaded: false };
              }
              return newSections;
            });
            setUploadedSections(prev => {
              const newUploads = { ...prev };
              delete newUploads[activeItem.index];
              return newUploads;
            });
          }
          setViewingFileUrl(null);
          if (activeItem.type === 'section') fetchSectionFiles();
        } else {
          const timestamp = new Date().getTime();
          if (activeItem.type === 'section') {
            setViewingFileUrl(prev => {
              const base = prev.split('?')[0];
              return `${base}?t=${timestamp}`;
            });
          }
          const newTotal = response.data.new_page_count;
          if (selectedPage > newTotal) {
            setSelectedPage(newTotal);
          }
          if (activeItem.type === 'section') {
            fetchSectionFiles();
            refreshSectionPageCounts();
          }
        }
      } else {
        alert("Failed to delete page: " + response.data.error);
      }
    } catch (error) {
      console.error("Error deleting page:", error);
      alert("Error contacting server.");
    }
  };

  const handleDeleteFile = async (filename) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/delete-section`, {
        data: { session_id: sessionId, filename: filename }
      });

      await fetchSectionFiles();
      await refreshSectionPageCounts();

      const response = await axios.get(`${API_BASE_URL}/api/section-files/${sessionId}/${activeItem.index}`);
      if (response.data.success && response.data.files.length === 0) {
        setSections(prev => {
          const newSections = [...prev];
          if (newSections[activeItem.index]) {
            newSections[activeItem.index] = { ...newSections[activeItem.index], uploaded: false };
          }
          return newSections;
        });
        setUploadedSections(prev => {
          const newUploads = { ...prev };
          delete newUploads[activeItem.index];
          return newUploads;
        });
        setViewingFileUrl(null);
      }
    } catch (error) {
      console.error("Failed Delete", error);
    }
  };

  const handleAddBlankPage = async (sectionNameOverride) => {
    try {
      setIsUploading(true);
      const response = await axios.post(`${API_BASE_URL}/api/add-blank-page`, {
        user_id: getUserId(),
        session_id: sessionId,
        section_index: activeItem.index,
        section_name: sectionNameOverride || "Section"
      });

      if (response.data.success) {
        setUploadedSections(prev => ({
          ...prev,
          [activeItem.index]: {
            name: response.data.filename,
            pageCount: response.data.page_count || 1,
          }
        }));
        await refreshSectionPageCounts();
        return response.data;
      }
    } catch (error) {
      console.error("Failed to create blank page:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const onAddBlankPageWrapper = async () => {
    const currentSectionName = sections[activeItem.index]?.name || "Section";
    const response = await handleAddBlankPage(currentSectionName);

    if (response && response.filename) {
      const savedName = response.filename;
      const newUrl = `${API_BASE_URL}/api/view/${sessionId}/${encodeURIComponent(savedName)}`;
      setViewingFileUrl(newUrl);
      setSectionFiles(prev => [...prev, savedName]);
      setTimeout(fetchSectionFiles, 500);
    }
  };

  const handleAppendUploadWrapped = async (file) => {
    if (!file) return;
    await handleSectionUpload(file);
    setTimeout(fetchSectionFiles, 500);
  };

  // ============================================================================
  // 4. ACTION HANDLERS (Uploads, Drag/Drop, Merge)
  // ============================================================================

  const handleIndexUpload = async (file) => {
    if (!sessionId) return showNotification('Session invalid', 'error');
    setIsUploading(true);
    setExtractionProgress(0);
    setExtractionStatus('Starting upload...');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/upload-index`, formData);
      const newSections = response.data.sections.filter(s => !s.found);
      const duplicateSections = response.data.sections.filter(s => s.found);

      setIndexFile(file);
      setSelectedPage(1);
      setIndexFileName(response.data.index_filename);
      setIndexPageCount(response.data.page_count || 0);
      setSections(newSections);
      await syncManifest({
        project_name:      projectName,
        sections_total:    newSections.length,
        sections_uploaded: 0,
        sections:          newSections.map(s => ({   // <-- ADD THIS
          name: s.name,
          page: s.page,
          found: s.found
        })),
      });
      setSkippedSections(duplicateSections);
      setExtractionProgress(100);
      setExtractionStatus('Complete!');

      showNotification(`Found ${newSections.length} sections`, 'success');
      if (newSections.length > 0) setActiveItem({ type: 'section', index: 0 });
    } catch (error) {
      showNotification('Index upload failed', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSectionUpload = async (file) => {
    const sectionIndex = activeItem.index;
    if (sectionIndex === null) return;

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('section_index', sectionIndex);
    formData.append('section_name', sections[sectionIndex].name);
    formData.append('session_id', sessionId);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/upload-section`, formData, {
        onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total)),
      });

      const filename = response.data.filename;
      const sectionName = sections[sectionIndex].name;
      console.log(sectionName);

      // CHECK FOR ANNEXURE & GENERATE STICKER
      if (sectionName.toLowerCase().includes('annexure')) {
        console.log("here");
        const match = sectionName.match(/Annexure\s*([a-zA-Z0-9-]+)/i);
        const labelText = match ? match[0].toUpperCase() : "ANNEXURE";

        const newSticker = {
          type: 'text',
          text: labelText,
          x: 0.5,
          y: 0.05,
          width: 0.35,
          height: 0.06,
          fontSize: 20,
          isBold: true,
          font: 'Times-Roman',
          pdf_filename: filename,
          page_number: 1,
          color: '#000000'
        };

        try {
          const stickerRes = await axios.post(`${API_BASE_URL}/api/overlays`, {
            session_id: sessionId,
            ...newSticker
          });
          if (stickerRes.data.success) {
            setOverlays(prev => [...prev, stickerRes.data.overlay || newSticker]);
          }
        } catch (stickerErr) {
          console.error("File uploaded, but sticker failed to save:", stickerErr);
        }
      }

      const newSections = [...sections];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        filename: filename,
        uploaded: true
      };
      setSections(newSections);
      setSelectedPage(1);

      setUploadedSections(prev => ({
        ...prev,
        [sectionIndex]: {
          name: filename,
          pageCount: response.data.page_count || 0
        }
      }));

      await refreshSectionPageCounts();
      const newUploadedCount = Object.keys(uploadedSections).length + 1; // +1 because state hasn't updated yet
      const currentUploaded = Object.keys(uploadedSections).length;
      // +1 only if this section wasn't already uploaded
      const wasAlreadyUploaded = uploadedSections[sectionIndex] !== undefined;
      const newCount = wasAlreadyUploaded ? currentUploaded : currentUploaded + 1;

      await syncManifest({
        sections_total:    sections.length,
        sections_uploaded: newCount,
        project_name:      projectName,
      });
      useEffect(() => {
        if (!sessionId || !projectName || projectName === "Untitled") return;
        syncManifest({ project_name: projectName });
      }, [projectName, sessionId]);
      setRefreshKey(prev => prev + 1);
      showNotification('Section uploaded successfully', 'success');

    } catch (error) {
      showNotification('Upload failed', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMergeDocuments = async () => {
    if (merging) return;
    setMerging(true);
    try {
      // Build all_page_number_positions: { filename: { x, y } }
      // Use page 1's position for each file as the representative position
      const all_page_number_positions = {};
      Object.entries(pageNumberPositions).forEach(([filename, pages]) => {
        all_page_number_positions[filename] = pages[1] || { x: 95, y: 4 };
      });

      const response = await axios.post(`${API_BASE_URL}/api/merge-documents`, {
        session_id: sessionId,
        sections: sections,
        all_page_number_positions,   // now correctly keyed by filename
        show_page_numbers: showPageNumbers
      });
      setMergedDocument(response.data);
      setActiveItem({ type: 'merged', index: null });
      setNotification('Merge complete!', 'success');
      setShowPageNumbers(false);
      setSelectedPage(1);
    } catch (error) {
      setNotification('Merge failed', 'error');
    } finally {
      setMerging(false);
    }
  };

  const handleSectionDelete = async (e, idx) => {
    e.stopPropagation();
    const file = uploadedSections[idx];
    if (!file) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/delete-section`, {
        data: { filename: file.name, session_id: sessionId }
      });

      setUploadedSections(prev => {
        const newUploads = { ...prev };
        delete newUploads[idx];
        return newUploads;
      });
      setSections(prev => {
        const newSections = [...prev];
        if (newSections[idx]) {
          newSections[idx] = { ...newSections[idx], uploaded: false };
        }
        return newSections;
      });
      await refreshSectionPageCounts();
      setNotification('File deleted', 'info');

    } catch (err) {
      console.error(err);
      setNotification('Delete failed', 'error');
    }
  };

  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setIsReordering(true);

    const reorderPayload = items.map(section => ({
      filename: section.filename || null,
      name: section.name
    }));

    try {
      const response = await axios.post(`${API_BASE_URL}/api/reorder-sections`, {
        session_id: sessionId,
        new_order: reorderPayload
      });

      if (response.data.success && response.data.filename_mapping) {
        const mapping = response.data.filename_mapping;
        const updatedSections = items.map((section) => {
          const mappingEntry = mapping.find(m => m.section_name === section.name);
          return {
            ...section,
            filename: mappingEntry ? mappingEntry.new_filename : section.filename,
            uploaded: !!mappingEntry?.new_filename
          };
        });
        const newUploadedSections = {};
        updatedSections.forEach((section, idx) => {
          if (section.filename) {
            newUploadedSections[idx] = { name: section.filename };
          }
        });
        setSections(updatedSections);
        setUploadedSections(newUploadedSections);
        setRefreshKey(prev => prev + 1);
        await new Promise(resolve => setTimeout(resolve, 100));
        setActiveItem({ type: 'section', index: result.destination.index });
        setIsReordering(false);
        showNotification('Sections reordered successfully', 'success');
      }
    } catch (error) {
      console.error("Failed to sync order", error);
      setNotification('Failed to save new order', 'error');
      setIsReordering(false);
      setActiveItem({ type: 'section', index: result.destination.index });
    }
  };

  const handleAddSection = () => {
    const name = window.prompt("Enter the name for the new section:");
    if (name && name.trim() !== "") {
      setSections(prev => [
        ...prev,
        { name: name.trim(), page: null, found: false }
      ]);
      setTimeout(() => {
        setActiveItem({ type: 'section', index: (sections || []).length });
      }, 100);
    }
  };

  const handleRotateCurrentPage = () => {
    if (selectedPage) {
      setPageRotations(prev => ({
        ...prev,
        [selectedPage]: ((prev[selectedPage] || 0) + 90) % 360
      }));
    }
  };

  // --- Drag & Drop for File Upload ---
  const handleDragOver = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];

      if (activeItem.type === 'index' && !indexFile) {
        handleIndexUpload(droppedFile);
      } else if (activeItem.type === 'section') {
        const allowedTypes = [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/msword"
        ];
        if (allowedTypes.includes(droppedFile.type)) {
          handleSectionUpload(droppedFile);
        } else {
          showNotification('Please upload a PDF or Word document', 'error');
        }
      }
    }
  }, [activeItem, indexFile, uploadedSections]);

  // --- Image Overlay Handling ---
  const triggerImageUpload = () => {
    const activeUrl = activeItem.type === 'section' ? viewingFileUrl : currentPreviewUrl;
    if (!activeUrl) return showNotification("Please select a document first", "error");
    imageInputRef.current.click();
  };

  const handleImageSelected = async (e) => {
    const file = e.target.files[0];
    console.log("handleImageSelected fired", { file, currentPreviewUrl, selectedPage });
    if (!file || !currentPreviewUrl) return;

    const activeUrl = activeItem.type === 'section' ? viewingFileUrl : currentPreviewUrl;
    if (!activeUrl) return showNotification("No document selected", "error");

    const currentFname = currentPreviewUrl.split('/').pop().split('?')[0];
    const localUrl = URL.createObjectURL(file);

    const getImageDimensions = (url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.width, h: img.height });
        img.src = url;
      });
    };

    const dims = await getImageDimensions(localUrl);
    const pageRatio = 0.707;
    const imageRatio = dims.h / dims.w;
    const initWidth = 0.25;
    const initHeight = initWidth * imageRatio * pageRatio;
    const tempId = `temp_${Date.now()}`;

    const tempOverlay = {
      id: tempId,
      page_number: selectedPage || 1,
      pdf_filename: currentFname,
      x: 0.35, y: 0.35,
      width: initWidth,
      height: initHeight,
      image_filename: 'preview',
      local_url: localUrl,
      is_temp: true
    };

    setOverlays(prev => [...prev, tempOverlay]);
    e.target.value = null;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('session_id', sessionId);
    formData.append('filename', currentFname);
    formData.append('page_number', selectedPage || 1);

    setIsModifying(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/overlays`, formData);
      if (res.data.success) {
        setOverlays(prev => prev.map(o => {
          if (o.id === tempId) {
            return {
              ...res.data.overlay,
              x: o.x, y: o.y,
              width: o.width !== initWidth ? o.width : initWidth,
              height: o.height !== initHeight ? o.height : initHeight
            };
          }
          return o;
        }));
        showNotification("Image saved", "success");
      }
    } catch (error) {
      setOverlays(prev => prev.filter(o => o.id !== tempId));
      showNotification("Failed to upload image", "error");
    } finally {
      setIsModifying(false);
    }
  };

  const handleUpdateOverlay = async (id, newProps, isFinal = false) => {
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, ...newProps } : o));

    if (isFinal && !String(id).startsWith('temp_')) {
      try {
        await axios.put(`${API_BASE_URL}/api/overlays`, {
          session_id: sessionId,
          id: id,
          ...newProps
        });
      } catch (error) {
        console.error("Failed to save position", error);
      }
    }
  };

  const handleDeleteOverlay = async (id) => {
    setOverlays(prev => prev.filter(o => o.id !== id));
    if (selectedOverlayId === id) setSelectedOverlayId(null);

    try {
      await axios.delete(`${API_BASE_URL}/api/overlays?session_id=${sessionId}&overlay_id=${id}`);
    } catch (error) {
      console.error("Failed to delete overlay", error);
    }
  };

  // ============================================================================
  // 5. EFFECTS
  // ============================================================================

  useEffect(() => {
    const createSession = async () => {
      try {
        const savedSessionId = localStorage.getItem('current_session_id');
        
        if (savedSessionId) {
          // Clear immediately to prevent loops
          localStorage.removeItem('current_session_id');
          
          // Restore session state from manifest
          const savedProjectName = localStorage.getItem('current_project_name');
          const savedSectionsTotal = parseInt(localStorage.getItem('current_sections_total') || '0');
          localStorage.removeItem('current_project_name');
          localStorage.removeItem('current_sections_total');
          
          setSessionId(savedSessionId);
          if (savedProjectName) setProjectName(savedProjectName);
          
          // Fetch full manifest from backend to restore sections
          try {
            const res = await axios.get(
              `${API_BASE_URL}/api/session-manifest/${savedSessionId}`
            );
            if (res.data.success && res.data.manifest) {
              const manifest = res.data.manifest;
              setProjectName(manifest.project_name || "Untitled");
            }
          } catch (e) {
            console.error("Could not load manifest:", e);
          }

          // Fetch the actual sections from the backend
          try {
            const sectionsRes = await axios.get(
              `${API_BASE_URL}/api/session-sections/${savedSessionId}`
            );
            if (sectionsRes.data.success) {
              setSections(sectionsRes.data.sections);
              setUploadedSections(sectionsRes.data.uploaded_sections);
              setIndexFileName(sectionsRes.data.index_filename);
              if (sectionsRes.data.index_filename) {
                setIndexFile(true); // Mark index as present
              }
            }
          } catch (e) {
            console.error("Could not restore sections:", e);
          }

          return;
        }
        
        // No saved session, create fresh
        const response = await axios.post(`${API_BASE_URL}/api/create-session`, {});
        setSessionId(response.data.session_id);
      } catch (error) {
        setNotification({ message: 'Backend disconnected', type: 'error' });
      }
    };
    createSession();
  }, []);

  useEffect(() => {
    if (sessionId) {
      axios.get(`${API_BASE_URL}/api/overlays?session_id=${sessionId}`)
        .then(res => setOverlays(res.data))
        .catch(err => console.error("Failed to load overlays", err));
    }
  }, [sessionId, refreshKey]);

  useEffect(() => {
    let pollInterval;
    if (isUploading && !indexFile && sessionId) {
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/progress/${sessionId}`);
          setExtractionProgress(response.data.progress);
          setExtractionStatus(response.data.status);
        } catch (error) {
          // silent fail
        }
      }, 800);
    }
    return () => clearInterval(pollInterval);
  }, [isUploading, indexFile, sessionId]);

  useEffect(() => {
    if (selectedPage) {
      const pageElement = document.getElementById(`page-wrapper-${selectedPage}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [selectedPage]);

  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      if (sessionId) {
        const url = `${API_BASE_URL}/api/cleanup-session`;
        const data = JSON.stringify({ session_id: sessionId });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [sessionId]);

  useEffect(() => {
    setScale(1.0);
    setPageRotations({});
    setSelectedPage(1);

    if (activeItem.type === 'section') {
      setNumPages(null);
      fetchSectionFiles();
    } else {
      setSectionFiles([]);
      setViewingFileUrl(currentPreviewUrl);
    }
  }, [activeItem, sessionId, currentPreviewUrl, fetchSectionFiles]);

 
// Auto-save case to dashboard
useEffect(() => {
  if (!sessionId) return;
  if (!sections) return; 
  const progress = (() => {
    if (!sections ||sections.length === 0) return indexFile ? 10 : 0;
    const uploadedCount = Object.keys(uploadedSections || {}).length;
    return Math.round(10 + (uploadedCount * (90 / sections.length)));
  })();

  if(progress === 0) return; 
  const saveCase = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/save-case`, {
        session_id: sessionId,
        title: projectName,
        progress: progress,
        status: mergedDocument ? "Ready to File" 
               : progress > 0 ? "In Progress" 
               : "Not Started",
        statusColor: mergedDocument 
          ? "bg-green-500/20 text-green-300"
          : progress > 0 
          ? "bg-blue-500/20 text-blue-300"
          : "bg-slate-500/20 text-slate-300",
        type: "Writ Petition",
        jurisdiction: jurisdiction,
        sections: sections,              // ← ADD
        uploadedSections: uploadedSections,
      });
    } catch (err) {
      console.error("Auto-save failed:", err);
    }
  };

  // Debounce: wait 1s after last change before saving
  const timer = setTimeout(saveCase, 1000);
  return () => clearTimeout(timer);

}, [sessionId, projectName, uploadedSections, mergedDocument, indexFile, sections]);

// Add this function inside useAppLogic
const loadExistingSession = useCallback(async (existingSessionId) => {
  try {
    setSessionId(existingSessionId);
    
    const res = await axios.get(`${API_BASE_URL}/api/get-session/${existingSessionId}`);
    if (res.data.success) {
      // Restore index
      if (res.data.has_index) {
        setIndexFileName(res.data.index_filename);
        setIndexFile(true);
      }
      
      // Restore project name
      if (res.data.case?.title) {
        setProjectName(res.data.case.title);
      }

      // Restore sections and uploaded state ← KEY FIX
      if (res.data.case?.sections) {
        setSections(res.data.case.sections);
      }
      if (res.data.case?.uploadedSections) {
        setUploadedSections(res.data.case.uploadedSections);
      }
    }
  } catch (err) {
    console.error("Failed to load existing session:", err);
  }
}, []);
  // ============================================================================
  // 6. RETURN API
  // ============================================================================
  return {
    // --- State Variables ---
    sessionId, setSessionId,  // ← add setSessionId
    projectName, setProjectName,
    currentView, setCurrentView,
    jurisdiction, setJurisdiction,  
    indexFile, setIndexFile, indexFileName, indexPageCount,
    sections, setSections,
    uploadedSections, setUploadedSections,
    skippedSections,
    activeItem, setActiveItem,
    pageNumberFontSize, setPageNumberFontSize,
    loadExistingSession,
    sectionFiles,
    viewingFileUrl, setViewingFileUrl,

    isUploading, uploadProgress,
    extractionProgress, extractionStatus,
    merging, mergedDocument,
    notification, showNotification,
    refreshKey, setRefreshKey,
    isModifying, setIsModifying,

    isDragging, setIsDragging,
    isReordering,
    isThumbnailsOpen, toggleThumbnails,

    // --- Viewer State & Functions ---
    scale, setScale,
    pageRotations, handleRotateCurrentPage,
    selectedPage, setSelectedPage,
    numPages, setNumPages,
    overlays, setOverlays,
    selectedOverlayId, setSelectedOverlayId,
    imageInputRef,
    showPageNumbers, togglePageNumbers,
    showPageNumbersForActiveFile,

    // Per-page position API: pass both to PDFEditorCanvas
    pageNumberPosition: pageNumberPositions,  // keep prop name for App.js compat
    setPageNumberPosition,
    getPageNumberPosition,      // (pageNum) => { x, y }

    // --- Computed ---
    currentPreviewUrl,
    activeName,
    isExtracting,
    canMerge,
    calculateOverallProgress,
    pageOffset,
    refreshSectionPageCounts,

    // --- Actions ---
    fetchSectionFiles,
    handleAddBlankPage,
    onAddBlankPageWrapper,
    handleAppendUploadWrapped,
    handleAddSection,
    handleDeleteOverlay,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleImageSelected,
    handleIndexUpload,
    handleMergeDocuments,
    handleOnDragEnd,
    handleSectionDelete,
    handleDeleteFile,
    handleSectionUpload,
    handleUpdateOverlay,
    triggerImageUpload,
    handleInsertPageLocally,
    handleDeleteCurrentPage,
  };
}